import React, { useState } from 'react';
import {
  FiUser,
  FiLock,
  FiPhone,
  FiCreditCard,
  FiMail,
  FiEye,
  FiEyeOff,
  FiUserPlus,
  FiArrowLeft,
  FiCheck,
  FiMapPin,
  FiBriefcase,
  FiCrosshair,
} from 'react-icons/fi';
import API, { getImagenUrl } from 'shared/api';
import MapaUbicacion from 'shared/components/MapaUbicacion';

function getEmpresaSlug(schemaName) {
  return (
    localStorage.getItem('agorax_empresa_slug') || (schemaName ? schemaName.replace(/_/g, '-') : '')
  );
}

function RegistroClienteCompleto({
  sessionId,
  schemaName,
  brandName,
  logoUrl,
  onRegistroExitoso,
  onVolverLogin,
}) {
  const [form, setForm] = useState({
    usuario: '',
    clave: '',
    nombre: '',
    apellido: '',
    telefono: '',
    cedula: '',
    email: '',
    tipo: 'natural',
    rif: '',
    nombre_empresa: '',
    lat: '',
    lng: '',
  });
  const [mostrarClave, setMostrarClave] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [obteniendoGPS, setObteniendoGPS] = useState(false);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleTelefono = (e) => {
    const val = e.target.value.replace(/\D/g, '');
    if (val.length <= 15) update('telefono', val);
  };

  const handleCedula = (e) => {
    const val = e.target.value.replace(/\D/g, '');
    if (val.length <= 12) update('cedula', val);
  };

  const handleRIF = (e) => {
    const val = e.target.value.toUpperCase();
    update('rif', val);
  };

  const setUbicacion = (lat, lng) => {
    update('lat', String(Number(lat).toFixed(7)));
    update('lng', String(Number(lng).toFixed(7)));
  };

  const obtenerGPS = () => {
    if (!navigator.geolocation) {
      setError('Tu navegador no soporta geolocalización.');
      return;
    }
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      setError(
        'La geolocalización requiere HTTPS en producción. Ingresa las coordenadas manualmente.'
      );
      return;
    }
    setObteniendoGPS(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUbicacion(pos.coords.latitude, pos.coords.longitude);
        setObteniendoGPS(false);
      },
      (err) => {
        const msg =
          err.code === err.PERMISSION_DENIED
            ? 'Permiso denegado. Activa la ubicación en tu navegador o ingresa las coordenadas manualmente.'
            : err.code === err.TIMEOUT
              ? 'Tiempo agotado. Intenta de nuevo o ingresa las coordenadas manualmente.'
              : `Error al obtener ubicación (${err.message}). Ingresa las coordenadas manualmente.`;
        setError(msg);
        setObteniendoGPS(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const validar = () => {
    if (!form.usuario.trim() || form.usuario.length < 3)
      return 'El usuario debe tener al menos 3 caracteres';
    if (!/^[a-zA-Z0-9_.]+$/.test(form.usuario))
      return 'El usuario solo permite letras, números, puntos y guiones bajos';
    if (!form.clave || form.clave.length < 6)
      return 'La contraseña debe tener al menos 6 caracteres';
    if (form.tipo === 'natural') {
      if (!form.nombre.trim()) return 'Ingresa tu nombre';
      if (!form.apellido.trim()) return 'Ingresa tu apellido';
    }
    if (form.tipo === 'juridico') {
      if (!form.nombre_empresa.trim() && !form.nombre.trim())
        return 'Ingresa el nombre de la empresa o razon social';
      if (!form.rif.trim()) return 'Ingresa el RIF de la empresa';
    }
    if (!form.telefono.trim() || form.telefono.length < 10)
      return 'Ingresa un teléfono válido (al menos 10 dígitos)';
    if (!form.cedula.trim() || form.cedula.length < 5)
      return 'Ingresa una cédula válida (al menos 5 dígitos)';
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
      return 'Ingresa un correo electrónico válido';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const msg = validar();
    if (msg) {
      setError(msg);
      return;
    }

    setEnviando(true);
    setError(null);
    try {
      const payload = {
        session_id: sessionId,
        schema_name: schemaName,
        usuario: form.usuario.trim(),
        clave: form.clave,
        nombre: form.nombre.trim() || form.nombre_empresa.trim(),
        apellido: form.apellido.trim(),
        telefono: form.telefono.trim(),
        cedula: form.cedula.trim(),
        email: form.email.trim() || undefined,
        tipo: form.tipo,
        rif: form.tipo === 'juridico' ? form.rif.trim() : undefined,
        nombre_empresa:
          form.tipo === 'juridico' ? form.nombre_empresa.trim() || undefined : undefined,
        lat: form.lat ? parseFloat(form.lat) : undefined,
        lng: form.lng ? parseFloat(form.lng) : undefined,
      };

      const slug = getEmpresaSlug(schemaName);
      console.log(
        '[DEBUG registro-completo] POST /registro/registrar-completo slug:',
        slug,
        'schema:',
        schemaName
      );

      const res = await API.post('/registro/registrar-completo', payload, {
        timeout: 15000,
      });
      if (res.data.status === 'success') {
        localStorage.setItem(`agorax_registro_${schemaName}`, 'true');
        onRegistroExitoso(res.data.data);
      } else {
        setError(res.data.message || 'Error al registrar');
      }
    } catch (err) {
      const data = err.response?.data;
      const status = err.response?.status;
      const code = err.code;
      const isTimeout = code === 'ECONNABORTED';
      const isNetErr = code === 'ERR_NETWORK';

      let msg;
      if (isTimeout) {
        msg = 'El servidor tardó demasiado en responder. Intenta de nuevo.';
      } else if (isNetErr) {
        msg =
          'No se pudo conectar con el servidor. ' +
          'Verifica tu conexión a internet e intenta de nuevo. ' +
          'Si el problema persiste, abre las Herramientas de Desarrollo (F12) > pestaña Network, ' +
          'reproduce el error y revisa si aparece una solicitud OPTIONS y POST a /registro/registrar-completo.';
      } else if (status) {
        msg = data?.detail || data?.message || `Error del servidor (${status}). Intenta de nuevo.`;
      } else {
        msg = 'Error de conexión. Intenta de nuevo.';
      }

      setError(msg);
      console.error('[REGISTRO-DIAG] Error en registro completo:', {
        timestamp: new Date().toISOString(),
        status,
        code,
        message: err.message,
        fullUrl: `${err.config?.baseURL || ''}${err.config?.url || ''}`,
        requestHeaders: err.config?.headers,
        responseData: data,
      });
    } finally {
      setEnviando(false);
    }
  };

  const isJuridico = form.tipo === 'juridico';
  const hasLat = form.lat && form.lng;
  const latNum = form.lat ? parseFloat(form.lat) : undefined;
  const lngNum = form.lng ? parseFloat(form.lng) : undefined;

  return (
    <div style={S.wrapper}>
      <style>{`
        @keyframes cli-spin { to { transform: rotate(360deg); } }
        .cli-input-wrap:focus-within { border-color: var(--color-secondary) !important; box-shadow: 0 0 0 3px rgba(41,82,204,0.08); }
        @media (max-width: 400px) {
          .cli-row { flex-direction: column !important; }
        }
      `}</style>
      <div style={S.scrollArea}>
        <div style={S.container}>
          <div style={S.card}>
            {/* Header Banner */}
            <div style={S.headerBanner}>
              <img
                src={
                  logoUrl
                    ? getImagenUrl(logoUrl)
                    : '/logo-agorax.jpeg'
                }
                alt={brandName}
                style={S.logoImg}
              />
              <h1 style={S.brand}>{brandName}</h1>
              <p style={S.subtitle}>Crea tu cuenta para comenzar</p>
            </div>

            {/* Error */}
            {error && (
              <div style={S.errorBanner} onClick={() => setError(null)} role="alert">
                <span style={{ flex: 1, fontSize: '12.5px', lineHeight: 1.4 }}>{error}</span>
                <span
                  style={{
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: '16px',
                    paddingLeft: '8px',
                  }}
                >
                  &times;
                </span>
              </div>
            )}

            <form onSubmit={handleSubmit} style={S.form}>
              {/* DATOS DE ACCESO */}
              <Section icon={<FiLock size={14} />} label="Datos de acceso" />

              <InputGroup icon={<FiUser size={15} />}>
                <input
                  type="text"
                  placeholder="Usuario"
                  value={form.usuario}
                  onChange={(e) => update('usuario', e.target.value)}
                  style={S.input}
                  autoComplete="username"
                  autoCapitalize="off"
                />
              </InputGroup>

              <InputGroup icon={<FiLock size={15} />}>
                <input
                  type={mostrarClave ? 'text' : 'password'}
                  placeholder="Contraseña"
                  value={form.clave}
                  onChange={(e) => update('clave', e.target.value)}
                  style={S.input}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setMostrarClave(!mostrarClave)}
                  style={S.togglePwd}
                  tabIndex={-1}
                  aria-label={mostrarClave ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {mostrarClave ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                </button>
              </InputGroup>

              {/* TIPO DE PERSONA */}
              <Section icon={<FiBriefcase size={14} />} label="Tipo de persona" />

              <div style={S.tipoToggle}>
                <button
                  type="button"
                  onClick={() => update('tipo', 'natural')}
                  style={{ ...S.tipoBtn, ...(form.tipo === 'natural' ? S.tipoBtnActive : {}) }}
                >
                  {form.tipo === 'natural' && <FiCheck size={13} />}
                  <FiUser size={14} />
                  Natural
                </button>
                <button
                  type="button"
                  onClick={() => update('tipo', 'juridico')}
                  style={{ ...S.tipoBtn, ...(form.tipo === 'juridico' ? S.tipoBtnActive : {}) }}
                >
                  {form.tipo === 'juridico' && <FiCheck size={13} />}
                  <FiBriefcase size={14} />
                  Jurídico
                </button>
              </div>

              {/* DATOS PERSONALES / EMPRESA */}
              <Section
                icon={<FiUser size={14} />}
                label={isJuridico ? 'Datos de la empresa' : 'Datos personales'}
              />

              {isJuridico ? (
                <>
                  <InputGroup icon={<FiBriefcase size={15} />}>
                    <input
                      type="text"
                      placeholder="Nombre de la empresa o razón social"
                      value={form.nombre_empresa}
                      onChange={(e) => update('nombre_empresa', e.target.value)}
                      style={S.input}
                      autoComplete="organization"
                    />
                  </InputGroup>

                  <div className="cli-row" style={S.row}>
                    <InputGroup icon={<FiCreditCard size={15} />} style={{ flex: 1 }}>
                      <input
                        type="text"
                        placeholder="RIF (ej: J-12345678-9)"
                        value={form.rif}
                        onChange={handleRIF}
                        style={S.input}
                        autoComplete="off"
                        maxLength={30}
                      />
                    </InputGroup>
                    <InputGroup icon={<FiPhone size={15} />} style={{ flex: 1 }}>
                      <input
                        type="tel"
                        placeholder="Teléfono"
                        value={form.telefono}
                        onChange={handleTelefono}
                        style={S.input}
                        autoComplete="tel"
                      />
                    </InputGroup>
                  </div>

                  <div className="cli-row" style={S.row}>
                    <InputGroup icon={<FiUser size={15} />} style={{ flex: 1 }}>
                      <input
                        type="text"
                        placeholder="Nombre del representante"
                        value={form.nombre}
                        onChange={(e) => update('nombre', e.target.value)}
                        style={S.input}
                        autoComplete="name"
                      />
                    </InputGroup>
                    <InputGroup icon={<FiCreditCard size={15} />} style={{ flex: 1 }}>
                      <input
                        type="text"
                        placeholder="Cédula representante"
                        value={form.cedula}
                        onChange={handleCedula}
                        style={S.input}
                        autoComplete="off"
                      />
                    </InputGroup>
                  </div>
                </>
              ) : (
                <>
                  <div className="cli-row" style={S.row}>
                    <InputGroup icon={<FiUser size={15} />} style={{ flex: 1 }}>
                      <input
                        type="text"
                        placeholder="Nombre"
                        value={form.nombre}
                        onChange={(e) => update('nombre', e.target.value)}
                        style={S.input}
                        autoComplete="given-name"
                      />
                    </InputGroup>
                    <InputGroup icon={<FiUser size={15} />} style={{ flex: 1 }}>
                      <input
                        type="text"
                        placeholder="Apellido"
                        value={form.apellido}
                        onChange={(e) => update('apellido', e.target.value)}
                        style={S.input}
                        autoComplete="family-name"
                      />
                    </InputGroup>
                  </div>

                  <div className="cli-row" style={S.row}>
                    <InputGroup icon={<FiPhone size={15} />} style={{ flex: 1 }}>
                      <input
                        type="tel"
                        placeholder="Teléfono"
                        value={form.telefono}
                        onChange={handleTelefono}
                        style={S.input}
                        autoComplete="tel"
                      />
                    </InputGroup>
                    <InputGroup icon={<FiCreditCard size={15} />} style={{ flex: 1 }}>
                      <input
                        type="text"
                        placeholder="Cédula"
                        value={form.cedula}
                        onChange={handleCedula}
                        style={S.input}
                        autoComplete="off"
                      />
                    </InputGroup>
                  </div>
                </>
              )}

              {/* CONTACTO */}
              <Section icon={<FiMail size={14} />} label="Contacto" />

              <InputGroup icon={<FiMail size={15} />}>
                <input
                  type="email"
                  placeholder="Correo electrónico (opcional)"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  style={S.input}
                  autoComplete="email"
                />
              </InputGroup>

              {/* UBICACION (juridico) */}
              {isJuridico && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Section icon={<FiMapPin size={14} />} label="Ubicación del negocio" />

                  <button
                    type="button"
                    onClick={obtenerGPS}
                    disabled={obteniendoGPS}
                    style={{
                      ...S.gpsBtn,
                      opacity: obteniendoGPS ? 0.7 : 1,
                      cursor: obteniendoGPS ? 'wait' : 'pointer',
                    }}
                  >
                    {obteniendoGPS ? (
                      <>
                        <span style={S.gpsSpinner} />
                        Obteniendo ubicación...
                      </>
                    ) : (
                      <>
                        <FiCrosshair size={14} />
                        Usar mi ubicación actual
                      </>
                    )}
                  </button>

                  {hasLat && (
                    <MapaUbicacion
                      lat={latNum}
                      lng={lngNum}
                      readOnly={false}
                      height="180px"
                      onLocationChange={setUbicacion}
                    />
                  )}

                  <div className="cli-row" style={S.row}>
                    <InputGroup icon={<span style={S.coordLabel}>Lat</span>} style={{ flex: 1 }}>
                      <input
                        type="text"
                        placeholder="Latitud"
                        value={form.lat}
                        onChange={(e) => update('lat', e.target.value)}
                        style={S.input}
                      />
                    </InputGroup>
                    <InputGroup icon={<span style={S.coordLabel}>Lng</span>} style={{ flex: 1 }}>
                      <input
                        type="text"
                        placeholder="Longitud"
                        value={form.lng}
                        onChange={(e) => update('lng', e.target.value)}
                        style={S.input}
                      />
                    </InputGroup>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={enviando}
                style={{
                  ...S.btnPrimary,
                  opacity: enviando ? 0.7 : 1,
                  cursor: enviando ? 'wait' : 'pointer',
                }}
              >
                {enviando ? (
                  <>
                    <span style={S.btnSpinner} />
                    Creando cuenta...
                  </>
                ) : (
                  <>
                    <FiUserPlus size={17} />
                    Crear cuenta
                  </>
                )}
              </button>
            </form>

            <div style={S.links}>
              <button onClick={onVolverLogin} style={S.linkBtn}>
                <FiArrowLeft size={13} />
                Ya tengo cuenta
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InputGroup({ icon, children, style }) {
  return (
    <div className="cli-input-wrap" style={{ ...S.inputGroup, ...style }}>
      <span style={S.inputIcon}>{icon}</span>
      {children}
    </div>
  );
}

function Section({ icon, label }) {
  return (
    <div style={S.section}>
      <div style={S.sectionIcon}>{icon}</div>
      <span style={S.sectionLabel}>{label}</span>
    </div>
  );
}

const S = {
  wrapper: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--color-bg, #f0f2f5)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    zIndex: 100,
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 'env(safe-area-inset-bottom, 16px)',
  },
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '16px 12px 32px',
  },
  card: {
    backgroundColor: 'var(--color-surface, #fff)',
    borderRadius: '24px',
    padding: '0',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 4px 32px rgba(11,26,74,0.08)',
    overflow: 'hidden',
  },
  headerBanner: {
    background:
      'linear-gradient(135deg, var(--color-primary, #0B1A4A) 0%, var(--color-secondary, #2952CC) 100%)',
    padding: '26px 20px 22px',
    textAlign: 'center',
    color: '#fff',
  },
  logoImg: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    objectFit: 'contain',
    marginBottom: '8px',
    background: '#fff',
    padding: '4px',
  },
  brand: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#fff',
    margin: '0 0 2px',
    letterSpacing: '-0.3px',
  },
  subtitle: {
    fontSize: '12.5px',
    color: 'rgba(255,255,255,0.7)',
    margin: 0,
  },
  errorBanner: {
    fontSize: '13px',
    color: '#dc2626',
    backgroundColor: '#fef2f2',
    borderBottom: '2px solid #fecaca',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    cursor: 'pointer',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    padding: '16px 16px 20px',
  },
  section: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    marginTop: '8px',
    marginBottom: '0',
  },
  sectionIcon: {
    width: '24px',
    height: '24px',
    borderRadius: '7px',
    backgroundColor: 'var(--color-bg, #f0f2f5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-secondary, #2952CC)',
  },
  sectionLabel: {
    fontSize: '10.5px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--color-text-muted, #9ca3af)',
  },
  inputGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: 'var(--color-bg, #f0f2f5)',
    borderRadius: '11px',
    border: '1.5px solid var(--color-border, #e2e4e9)',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  inputIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    color: 'var(--color-text-muted, #9ca3af)',
    paddingLeft: '10px',
    flexShrink: 0,
  },
  input: {
    flex: 1,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: 'var(--color-text, #111827)',
    minWidth: 0,
    padding: '10px 12px 10px 0',
    lineHeight: '1.4',
  },
  row: { display: 'flex', gap: '7px' },
  togglePwd: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 10px 4px 0',
    color: 'var(--color-text-muted, #9ca3af)',
    display: 'flex',
    alignItems: 'center',
  },
  tipoToggle: { display: 'flex', gap: '7px' },
  tipoBtn: {
    flex: 1,
    padding: '10px 10px',
    borderRadius: '11px',
    border: '1.5px solid var(--color-border, #e2e4e9)',
    backgroundColor: 'var(--color-bg, #f0f2f5)',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    color: 'var(--color-text-secondary, #6b7280)',
    transition: 'all 0.15s',
  },
  tipoBtnActive: {
    borderColor: 'var(--color-secondary, #2952CC)',
    backgroundColor: '#eef1ff',
    color: 'var(--color-secondary, #2952CC)',
    boxShadow: '0 0 0 3px rgba(41,82,204,0.08)',
  },
  gpsBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '10px',
    borderRadius: '11px',
    border: '1.5px dashed var(--color-border, #e2e4e9)',
    backgroundColor: 'var(--color-bg, #f0f2f5)',
    fontSize: '12.5px',
    fontWeight: 600,
    fontFamily: 'inherit',
    color: 'var(--color-secondary, #2952CC)',
    transition: 'all 0.15s',
    width: '100%',
  },
  gpsSpinner: {
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    border: '2px solid rgba(41,82,204,0.2)',
    borderTopColor: 'var(--color-secondary, #2952CC)',
    animation: 'cli-spin 0.6s linear infinite',
    display: 'inline-block',
  },
  coordLabel: {
    fontSize: '10px',
    fontWeight: 700,
    color: 'var(--color-text-muted, #9ca3af)',
    width: '26px',
    textAlign: 'center',
  },
  btnPrimary: {
    padding: '13px',
    borderRadius: '13px',
    border: 'none',
    backgroundColor: 'var(--color-secondary, #2952CC)',
    color: '#fff',
    fontSize: '14.5px',
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: '6px',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '7px',
    transition: 'opacity 0.2s, transform 0.1s',
    boxShadow: '0 4px 14px rgba(41,82,204,0.25)',
  },
  btnSpinner: {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    animation: 'cli-spin 0.6s linear infinite',
    display: 'inline-block',
  },
  links: { padding: '0 16px 14px', textAlign: 'center' },
  linkBtn: {
    background: 'none',
    border: 'none',
    fontSize: '13px',
    color: 'var(--color-secondary, #2952CC)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 600,
    padding: '4px 8px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
};

export default RegistroClienteCompleto;
